import React, { useCallback, useRef, useState } from 'react'
import styled from '@emotion/styled'
import toast from 'react-hot-toast'
import { doc, setDoc } from 'firebase/firestore'
import { ref, uploadBytes } from 'firebase/storage'
import {
  buildProfileImagePath,
  createProfileImageKey,
  getProfileImageUploadMetadata,
  PROFILE_IMAGE_FILE_ACCEPT
} from '../../../pure-js/libs/ProfileImage'
import { Collections } from '../../../pure-js/libs/Collections'
import { usePortalAuth } from '../hooks/usePortalAuth'
import { usePortalDisplayName } from '../hooks/usePortalDisplayName'
import { usePortalEnrollmentUser } from '../hooks/usePortalEnrollmentUser'
import { invalidateProfileImageCache, seedProfileImageCache } from '../hooks/useProfileImage'
import useAppState from '../hooks/useAppState'
import { getGrooverText } from '../libs/GrooverTextRepository'
import { db, storage } from '../libs/Firebase'
import { useMounted } from '../hooks/useMounted'
import { safeAsync } from '../utils/safeAsync'
import GrooverText from '../components/GrooverText'
import { ProfileAvatar } from '../components/ProfileAvatar'
import { ProfileImageEditor } from '../components/ProfileImageEditor'

const PageContainer = styled.div`
  background: #ffffff;
  color: #1f2937;
  width: 100%;
  min-width: 0;
`

const MainContent = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 32px 40px;
  box-sizing: border-box;

  @media (min-width: 769px) {
    max-width: 720px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    padding: 16px 12px;
    min-width: 0;
  }
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 24px 0;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`

const Section = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 32px 24px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
`

const SectionHeading = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  align-self: flex-start;
`

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`

const ActionButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid var(--portal-primary, #7c3aed);
  background: #ffffff;
  color: var(--portal-primary, #7c3aed);
  transition:
    background 0.2s,
    opacity 0.2s;

  &:hover:not(:disabled) {
    background: rgba(var(--portal-primary-rgb, 124, 58, 237), 0.08);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const ClearButton = styled(ActionButton)`
  border-color: #d1d5db;
  color: #6b7280;

  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
`

const HiddenInput = styled.input`
  display: none;
`

const BusyOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
`

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid rgba(var(--portal-primary-rgb, 124, 58, 237), 0.15);
  border-top-color: var(--portal-primary, #7c3aed);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  gap: 16px;
`

const LoadingText = styled.div`
  font-size: 16px;
  color: #6b7280;
`

const avatarFallbackLetter = (displayName: string | undefined, portalName: string | undefined): string => {
  const name = displayName?.trim() || portalName?.trim()
  return name?.[0]?.toUpperCase() || 'U'
}

export const PortalProfilePage: React.FC = () => {
  const { portalUser, portalClaimsReady, isViewAs } = usePortalAuth()
  const enrollmentUserId = portalClaimsReady ? (portalUser?.enrollmentUserId ?? null) : null
  const { displayName } = usePortalDisplayName(enrollmentUserId)
  const { enrollmentUser, isLoading } = usePortalEnrollmentUser(enrollmentUserId)
  const { state } = useAppState()
  const selectedLanguage = state.selectedLanguage
  const getText = useCallback((key: string) => getGrooverText(key, selectedLanguage), [selectedLanguage])
  const isMounted = useMounted()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [cropFile, setCropFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)

  const profileImagePath = enrollmentUser?.profileImagePath
  const hasImage = !!profileImagePath?.trim()
  const canManageImage = !!enrollmentUserId && !isViewAs
  const fallbackLetter = avatarFallbackLetter(displayName, portalUser?.name)

  const handlePickFile = () => {
    if (!canManageImage || busy) {
      return
    }
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    event.target.value = ''
    if (!file || !canManageImage) {
      return
    }
    setCropFile(file)
  }

  const handleValidationError = useCallback(
    (textKey: string) => {
      toast.error(getText(textKey))
      setCropFile(null)
    },
    [getText]
  )

  const handleCropComplete = async (blob: Blob) => {
    if (!enrollmentUserId) {
      return
    }

    const previousPath = profileImagePath?.trim() || ''
    const key = createProfileImageKey()
    const path = buildProfileImagePath(key)
    let pointerWritten = false
    setBusy(true)

    try {
      // setDoc resolves to void → undefined; safeAsync only returns null on unmount
      const pointerResult = await safeAsync(
        () =>
          setDoc(doc(db, Collections.ENROLLMENT_USERS, enrollmentUserId), { profileImagePath: path }, { merge: true }),
        isMounted,
        { logContext: 'portalProfileImagePointer' }
      )
      if (pointerResult === null) {
        return
      }
      pointerWritten = true

      const uploadResult = await safeAsync(
        () => uploadBytes(ref(storage, path), blob, getProfileImageUploadMetadata()),
        isMounted,
        { logContext: 'portalProfileImageUpload' }
      )
      if (uploadResult === null) {
        return
      }

      if (previousPath) {
        invalidateProfileImageCache(previousPath)
      }
      seedProfileImageCache(path, blob)
    } catch {
      // E1: bytes upload failed after the pointer was written. Revert the pointer so it
      // never dangles at a Storage object that was never created — a retry then starts clean.
      if (pointerWritten) {
        invalidateProfileImageCache(path)
        try {
          await safeAsync(
            () =>
              setDoc(
                doc(db, Collections.ENROLLMENT_USERS, enrollmentUserId),
                { profileImagePath: previousPath },
                { merge: true }
              ),
            isMounted,
            { logContext: 'portalProfileImagePointerRollback' }
          )
        } catch {
          // Rollback write also failed — the pointer may remain dangling until the next
          // successful upload or clear; the failure toast below still surfaces the error.
        }
      }
      toast.error(getText('profileImageErrorUploadFailed'))
    } finally {
      if (isMounted()) {
        setBusy(false)
        setCropFile(null)
      }
    }
  }

  const handleClear = async () => {
    if (!enrollmentUserId || !hasImage || busy) {
      return
    }

    const previousPath = profileImagePath?.trim() || ''
    setBusy(true)

    try {
      // setDoc resolves to void → undefined; safeAsync only returns null on unmount
      const result = await safeAsync(
        () =>
          setDoc(doc(db, Collections.ENROLLMENT_USERS, enrollmentUserId), { profileImagePath: '' }, { merge: true }),
        isMounted,
        { logContext: 'portalProfileImageClear' }
      )
      if (result === null) {
        return
      }
      if (previousPath) {
        invalidateProfileImageCache(previousPath)
      }
    } catch {
      toast.error(getText('profileImageErrorClearFailed'))
    } finally {
      if (isMounted()) {
        setBusy(false)
      }
    }
  }

  if (!portalClaimsReady || isLoading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>{getText('portalDashboardLoading')}</LoadingText>
        </LoadingContainer>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <MainContent>
        <PageTitle>
          <GrooverText textKey="portalProfileTitle" />
        </PageTitle>

        <Section aria-busy={busy}>
          {busy && (
            <BusyOverlay aria-hidden>
              <LoadingSpinner />
            </BusyOverlay>
          )}

          <SectionHeading>
            <GrooverText textKey="portalProfilePhotoHeading" />
          </SectionHeading>

          <ProfileAvatar profileImagePath={profileImagePath} fallbackLetter={fallbackLetter} size={128} />

          <ActionsRow>
            <ActionButton type="button" onClick={handlePickFile} disabled={!canManageImage || busy}>
              <GrooverText textKey={hasImage ? 'profileImageReplace' : 'profileImageUpload'} />
            </ActionButton>
            {hasImage && (
              <ClearButton type="button" onClick={() => void handleClear()} disabled={!canManageImage || busy}>
                <GrooverText textKey="profileImageClear" />
              </ClearButton>
            )}
          </ActionsRow>

          <HiddenInput ref={fileInputRef} type="file" accept={PROFILE_IMAGE_FILE_ACCEPT} onChange={handleFileChange} />
        </Section>
      </MainContent>

      {cropFile && (
        <ProfileImageEditor
          file={cropFile}
          onClose={() => setCropFile(null)}
          onComplete={(blob) => void handleCropComplete(blob)}
          onValidationError={handleValidationError}
        />
      )}
    </PageContainer>
  )
}
